import { useEffect, useRef, useState } from "react";
import type { Match } from "@/types/match";
import { api, ApiError } from "@/services/api";
import { LIVE_POLL_INTERVAL_MS } from "@/services/endpoints";

interface UseLiveMatchesResult {
  matches: Match[];
  isLoading: boolean;
  error: ApiError | Error | null;
  refetch: () => void;
}

/**
 * Polls the live-matches endpoint on an interval to simulate real-time score
 * updates without a persistent connection. Swap the interval body for a
 * WebSocket/SSE subscription later — consumers of this hook won't need to
 * change at all.
 */
export function useLiveMatches(pollIntervalMs: number = LIVE_POLL_INTERVAL_MS): UseLiveMatchesResult {
  const [matches, setMatches] = useState<Match[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async (isBackground = false) => {
    if (!isBackground) setIsLoading(true);
    try {
      const data = await api.getLiveMatches();
      setMatches(data);
      setError(null);
    } catch (err) {
      // On background polls, keep showing stale data rather than clearing
      // the screen — only surface the error if we have nothing to show yet.
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load(false);
    timerRef.current = setInterval(() => load(true), pollIntervalMs);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollIntervalMs]);

  return { matches, isLoading, error, refetch: () => load(false) };
}
